import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { DocPicker } from "@/components/DocPicker";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Loader2, Sparkles, Download, ArrowRight, Save } from "lucide-react";
import { AIText } from "@/components/AIText";

const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function PremiumChange() {
  const { t, lang } = useI18n();
  const [prev, setPrev] = useState("");
  const [ren, setRen] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!prev || !ren) { toast.error(t("select_both_docs")); return; }
    if (prev === ren) { toast.error(t("choose_two_diff_docs")); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/analysis/premium-change", { previous_id: prev, renewal_id: ren, language: lang });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); }
    finally { setLoading(false); }
  };

  const exportPdf = () => downloadReport({
    title: "Premium Change Analysis", language: lang,
    subtitle: `${result.previous.name} → ${result.renewal.name}`,
    meta: result.computed ? { "Previous premium": money(result.computed.previous), "New premium": money(result.computed.new),
      "Difference": `${result.computed.difference >= 0 ? "+" : ""}${money(result.computed.difference)}`, "Change": `${result.computed.percent}%` } : null,
    sections: [{ heading: "Analysis", body: result.explanation }],
  });

  const c = result?.computed;
  const up = c && c.difference >= 0;

  const savePrem = async () => {
    try {
      await api.post("/saved-analyses", {
        kind: "premium_change",
        title: `Premium change: ${result.previous.name} → ${result.renewal.name}`,
        content: result.explanation,
        meta: c ? { previous: c.previous, new: c.new, difference: c.difference, percent: c.percent } : null,
      });
      toast.success(t("saved_to_analyses"));
    } catch { toast.error(t("could_not_save")); }
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><TrendingUp className="h-7 w-7 text-brandblue" /> {t("premium_change_title")}</h1>
      <p className="mt-1 text-slate-500">{t("premium_change_sub")}</p>

      <div className="mt-6 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-end gap-4">
          <div><label className="text-sm font-medium text-navy">{t("previous_policy")}</label><div className="mt-1.5"><DocPicker testId="prem-prev" value={prev} onChange={setPrev} exclude={ren} placeholder={t("previous_policy_ph")} /></div></div>
          <ArrowRight className="hidden md:block h-5 w-5 text-slate-300 mb-3" />
          <div><label className="text-sm font-medium text-navy">{t("renewal_new_policy")}</label><div className="mt-1.5"><DocPicker testId="prem-ren" value={ren} onChange={setRen} exclude={prev} placeholder={t("renewal_policy_ph")} /></div></div>
        </div>
        <Button data-testid="prem-run" onClick={run} disabled={loading} className="mt-4 rounded-full brand-gradient text-white">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("analyzing")}</> : <><Sparkles className="h-4 w-4 mr-2" />{t("analyze_change")}</>}
        </Button>
      </div>

      {result && (
        <div className="mt-6 space-y-6">
          {c && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label={t("st_previous")} value={money(c.previous)} />
              <Stat label={t("st_new")} value={money(c.new)} />
              <Stat label={t("st_difference")} value={`${up ? "+" : ""}${money(c.difference)}`} accent={up ? "up" : "down"} />
              <Stat label={t("st_change")} value={`${up ? "+" : ""}${c.percent}%`} accent={up ? "up" : "down"} />
            </div>
          )}
          {!c && (
            <div className="rounded-2xl bg-amber-50/60 border border-amber-100 p-4 text-sm text-amber-700">
              {t("premium_no_amount")}
            </div>
          )}
          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brandteal" /> {t("what_changed")}</h2>
              <div className="flex gap-2">
                <Button data-testid="prem-save" variant="outline" onClick={savePrem} className="rounded-full"><Save className="h-4 w-4 mr-2" />{t("save")}</Button>
                <Button data-testid="prem-export" variant="outline" onClick={exportPdf} className="rounded-full"><Download className="h-4 w-4 mr-2" />{t("export_pdf")}</Button>
              </div>
            </div>
            <div className="mt-3"><AIText>{result.explanation}</AIText></div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-4">
    <p className="text-xs text-slate-400">{label}</p>
    <p className={`mt-1 font-display text-2xl font-semibold flex items-center gap-1 ${accent === "up" ? "text-amber-600" : accent === "down" ? "text-emerald-600" : "text-navy"}`}>
      {accent === "up" && <TrendingUp className="h-5 w-5" />}{accent === "down" && <TrendingDown className="h-5 w-5" />}{value}
    </p>
  </div>
);
